/**
 * Recorridos guiados del POS. Cada pantalla (y cada ventana importante, como el cobro) tiene el suyo y se
 * ofrece una sola vez por usuario (el servidor guarda cuáles ya vio, ver `pos/tours`).
 *
 * Un paso sin `selector` sale al centro; si el selector no se encuentra en pantalla (opción apagada, vista
 * móvil) el paso se salta. `texto` filtra, entre los elementos del selector, el que contiene ese texto.
 *
 * El negocio cambia lo que se explica: un paso puede llevar `solo` (un tipo o una lista) y su `titulo` o
 * `cuerpo` puede ser un objeto `{ defecto, restaurante, retail, drogueria }`. Un recorrido con `aparece` no
 * depende de la ruta: arranca cuando ese selector se ve en pantalla (una ventana que el usuario abrió).
 */
export const TIPOS = ["retail", "restaurante", "drogueria"];

/** Tipo de negocio a partir de los getters del store (`isRestaurant`, `isDrugStore`); lo demás es retail. */
export function tipoDeNegocio(getters) {
  if (getters && getters.isRestaurant) return "restaurante";
  if (getters && getters.isDrugStore) return "drogueria";
  return "retail";
}

const fin = {
  titulo: "Listo",
  cuerpo: "Puedes volver a ver este recorrido cuando quieras desde tu cuenta (arriba a la derecha) → «Ver recorrido de esta pantalla».",
};

const finVentana = { titulo: "Listo", cuerpo: "Esta explicación sale solo la primera vez que abres esta ventana." };

const LECTOR = "Con el lector de códigos de barras, Enter agrega el producto exacto y el cursor vuelve aquí para escanear el siguiente.";

export const TOURS = {
  inicio: {
    ruta: /^\/pos\/?$/,
    pasos: [
      {
        titulo: "Bienvenido al punto de venta",
        cuerpo: {
          restaurante: "Te mostramos en un minuto cómo tomar un pedido: mesa, platos, cocina y cobro. Es solo una vez; puedes saltarlo.",
          drogueria: "Te mostramos en un minuto cómo vender en la droguería: búsqueda de medicamentos, fórmula médica, lotes y cobro. Es solo una vez; puedes saltarlo.",
          defecto: "Te mostramos en un minuto cómo vender: buscar o escanear productos, el cliente y el cobro. Es solo una vez; puedes saltarlo.",
        },
      },
      {
        selector: "#core-navigation-drawer",
        titulo: "Menú",
        cuerpo: "Desde aquí vas a la venta, al cierre de turno, a productos agotados, a los pedidos, a los egresos, a las tarjetas de regalo, a los reportes y a la configuración de la caja.",
        lado: "right",
      },
      {
        selector: "#search_prd .v-input",
        titulo: "Busca el producto",
        cuerpo: {
          restaurante: "Escribe el nombre o la referencia del plato, o ve por categorías aquí abajo. " + LECTOR,
          drogueria: "Escribe el nombre, el principio activo, la marca o el código EAN del medicamento. " + LECTOR,
          defecto: "Escribe el nombre o la referencia, o pasa el lector de códigos de barras. " + LECTOR,
        },
        lado: "right",
      },
      {
        selector: ".chips-categorias",
        titulo: "Categorías",
        cuerpo: "Filtra por categoría. Las que tienen una flecha guardan subcategorías; «Todas» quita el filtro.",
        lado: "bottom",
      },
      {
        selector: ".chip-avanzada",
        solo: "drogueria",
        titulo: "Búsqueda avanzada",
        cuerpo: "Busca por EAN, nombre o proveedor y mira de una vez las existencias, si requiere fórmula, si es controlado y si maneja lote.",
        lado: "bottom",
      },
      {
        selector: ".tarjeta__boton",
        titulo: "Agrega al pedido",
        cuerpo: {
          restaurante: "Toca el plato para agregarlo. Si tiene opciones o adicionales (término, salsas, sin cebolla) se abre una ventana para elegirlos. «Agotado» quiere decir que no se puede vender.",
          drogueria: "Toca el producto para agregarlo. Verás su principio activo y si está agotado. Si maneja lote, se abre una ventana para elegir de cuál lote sale.",
          defecto: "Toca el producto para agregarlo. Si tiene variantes (talla, color) u opciones, se abre una ventana para elegirlas. «Agotado» quiere decir que no se puede vender.",
        },
        lado: "right",
      },
      {
        selector: ".orderType",
        titulo: "Tipo de pedido",
        cuerpo: {
          restaurante: "«Directo» es para cobrar ya. «En mesa» deja la cuenta abierta en una mesa y se cobra al final. «Domicilio» pide los datos de entrega y el valor del domicilio.",
          defecto: "«Directo» es la venta normal de mostrador. «Domicilio» pide los datos de entrega y el valor del domicilio. Cambia lo que el sistema te pide antes de cobrar.",
        },
        lado: "left",
      },
      {
        selector: ".mesa-fila:not(.entrega-fila)",
        solo: "restaurante",
        titulo: "Mesa",
        cuerpo: "Con «En mesa» toca aquí para elegir la mesa del pedido. Para cambiarla o quitarla, usa la X.",
        lado: "left",
      },
      {
        selector: ".fila-chips",
        titulo: "Cliente, nota y más",
        cuerpo: {
          restaurante: "«Cliente» es opcional: úsalo si necesita factura con sus datos. «Nota» es un comentario para todo el pedido. En «Más» están la propina, el cupón de descuento y la factura electrónica.",
          defecto: "«Cliente» es opcional: úsalo si necesita factura con sus datos. «Nota» es un comentario para todo el pedido. En «Más» están el cupón de descuento y la factura electrónica (según la configuración).",
        },
        lado: "left",
      },
      {
        selector: ".linea-pedido",
        titulo: "Lo que lleva el pedido",
        cuerpo: {
          restaurante: "Cada línea muestra cantidad y precio. El lápiz edita (cantidad, descuento, opciones) y la X la quita. Lo que ya se envió a cocina queda marcado «Enviado» y solo la caja lo anula, con motivo.",
          defecto: "Cada línea muestra cantidad y precio. El lápiz edita (cantidad, descuento) y la X la quita. Si el pedido ya se guardó o se imprimió, cambiar o quitar una línea pide un motivo.",
        },
        lado: "left",
      },
      {
        solo: "drogueria",
        titulo: "Fórmula médica y controlados",
        cuerpo: "Los medicamentos que la requieren se marcan «Requiere fórmula» o «Controlado». Toca el icono de receta (o la franja del médico) para registrar el nombre y la tarjeta profesional; en los controlados el médico es obligatorio para poder cobrar.",
      },
      {
        selector: ".acciones-pedido",
        titulo: "Acciones del pedido",
        cuerpo: {
          restaurante: "Imprime la cuenta, manda la «Comanda» a la cocina o al bar (si está configurada) o anula el pedido completo. Solo se activan cuando hay productos.",
          defecto: "Imprime la cuenta o anula el pedido completo (pide un motivo). Solo se activan cuando hay productos.",
        },
        lado: "left",
      },
      {
        selector: "#totals_section",
        titulo: "Totales",
        cuerpo: {
          restaurante: "Impuestos, propina y domicilio, al instante. El total a pagar está en el botón verde.",
          defecto: "Impuestos y, si aplican, el domicilio, al instante. El total a pagar está en el botón verde.",
        },
        lado: "left",
      },
      {
        selector: "#cart_options button",
        texto: "Guardar",
        titulo: "Guardar",
        cuerpo: {
          restaurante: "Guarda el pedido sin cobrar: la cuenta queda abierta en «Pendientes» y puedes seguir agregando productos hasta el momento de cobrar.",
          defecto: "Guarda la venta sin cobrar. Queda en «Pendientes» para retomarla después, por ejemplo si el cliente va por su billetera.",
        },
        lado: "top",
      },
      {
        selector: "#btn-cobrar",
        titulo: "Cobrar",
        cuerpo: "Cuando el pedido esté listo, aquí eliges el medio de pago y se genera el recibo. La primera vez que lo abras te explicamos esa ventana.",
        lado: "top",
      },
      {
        selector: ".acc-header",
        texto: "Pendientes",
        titulo: "Pedidos pendientes",
        cuerpo: {
          restaurante: "Las cuentas que dejaste abiertas y los pedidos que envían los meseros. Ábrelos para seguir agregando o cobrar.",
          defecto: "Las ventas que dejaste guardadas para seguir después o para cobrar luego. El número indica cuántas hay.",
        },
        lado: "bottom",
      },
      {
        selector: ".acc-header",
        texto: "Mesas",
        solo: "restaurante",
        titulo: "Mesas",
        cuerpo: "El mapa de mesas por zona: mira cuáles están ocupadas.",
        lado: "bottom",
      },
      {
        selector: ".acc-header",
        texto: "Sincronizar",
        titulo: "Sincronizar",
        cuerpo: "Sube las ventas de esta caja al servidor y baja los cambios de productos y precios. Hazlo si cambiaste algo en el sistema y no lo ves aquí.",
        lado: "bottom",
      },
      {
        selector: ".acc-header",
        texto: "Ayuda",
        titulo: "Ayuda",
        cuerpo: "Videos tutoriales para resolver dudas sin salir del POS.",
        lado: "bottom",
      },
      { selector: "#btn-menu-profile", titulo: "Tu cuenta", cuerpo: "Aquí cierras sesión y vuelves a ver los recorridos.", lado: "bottom" },
      fin,
    ],
  },

  // Ventanas: arrancan solas cuando el usuario las abre por primera vez.
  cobro: {
    ruta: /^\/pos\/?$/,
    aparece: ".cobro__encabezado",
    pasos: [
      { selector: ".cobro__encabezado", titulo: "Total a pagar", cuerpo: "El valor de la venta. Cierra esta ventana con la X si necesitas volver al pedido.", lado: "bottom" },
      {
        selector: ".cobro__resumen",
        titulo: "Pagado y saldo",
        cuerpo: "A medida que registras pagos aquí ves cuánto va pagado y cuánto falta. Solo se puede cobrar cuando el saldo llega a cero.",
        lado: "bottom",
      },
      {
        selector: ".cobro__metodos",
        titulo: "Medios de pago",
        cuerpo: {
          restaurante: "Elige cómo paga el cliente (efectivo, tarjeta, transferencia…) y escribe el valor. Puedes combinar varios medios en una misma cuenta.",
          defecto: "Elige cómo paga el cliente (efectivo, tarjeta, transferencia, tarjeta de regalo o puntos si están activos) y escribe el valor. Puedes combinar varios medios en una misma venta.",
        },
        lado: "right",
      },
      {
        selector: ".cobro__pie .v-input--switch",
        titulo: "Imprimir el recibo",
        cuerpo: "Si está activo, el recibo sale al cobrar. Apágalo si el cliente no lo quiere.",
        lado: "top",
      },
      {
        selector: ".cobro__pie button",
        texto: "Separar cuenta",
        solo: "restaurante",
        titulo: "Separar cuenta",
        cuerpo: "Cuando la mesa paga por partes: cada cliente escoge lo suyo y paga por separado.",
        lado: "top",
      },
      {
        selector: ".cobro__pie button",
        texto: "Separar cuenta",
        solo: ["retail", "drogueria"],
        titulo: "Separar cuenta",
        cuerpo: "Si dos personas pagan una misma venta, cada una puede pagar lo suyo por separado.",
        lado: "top",
      },
      {
        selector: ".cobro__pie button",
        texto: "Pagar",
        titulo: "Cobrar",
        cuerpo: "Se activa cuando el saldo es cero. Registra la venta, genera el recibo y deja la caja lista para el siguiente cliente.",
        lado: "top",
      },
      finVentana,
    ],
  },
  mesas: {
    ruta: /^\/pos\/?$/,
    aparece: ".mesas__encabezado",
    solo: "restaurante",
    pasos: [
      { selector: ".mesas__encabezado", titulo: "Mesas", cuerpo: "El estado de las mesas del local. Aquí solo se mira; para tomar un pedido elige «En mesa» y la mesa desde el pedido.", lado: "bottom" },
      { selector: ".mesas__zonas", titulo: "Zonas", cuerpo: "Salón, terraza, barra… Cambia de zona para ver sus mesas.", lado: "bottom" },
      finVentana,
    ],
  },
  busquedaAvanzada: {
    ruta: /^\/pos\/?$/,
    aparece: ".busqueda-med__encabezado",
    solo: "drogueria",
    pasos: [
      { selector: ".busqueda-med__filtros", titulo: "Busca como necesites", cuerpo: "Escribe nombre, principio activo, marca, proveedor o EAN. Los filtros afinan el resultado cuando hay muchos parecidos.", lado: "bottom" },
      {
        titulo: "Lee las etiquetas",
        cuerpo: "Cada resultado dice si requiere fórmula, si es controlado, si maneja lote y cuántas existencias hay (en rojo si está agotado). Tócalo para agregarlo al pedido.",
      },
      finVentana,
    ],
  },
  formula: {
    ruta: /^\/pos\/?$/,
    aparece: ".receta__encabezado",
    solo: "drogueria",
    pasos: [
      {
        selector: ".receta__encabezado",
        titulo: "Fórmula médica",
        cuerpo: "Registra quién formuló el medicamento. Es lo que queda como soporte de la venta de productos con fórmula o controlados.",
        lado: "bottom",
      },
      {
        titulo: "Busca al médico",
        cuerpo: "Escribe la tarjeta profesional y toca buscar: si ya lo registraste antes, sus datos se llenan solos. Si no existe, completa los datos y queda guardado para la próxima.",
      },
      finVentana,
    ],
  },

  turnos: {
    ruta: /^\/pos\/(turnos|close-turn|turns|turns-old)\/?$/,
    pasos: [
      { titulo: "Turnos y cierre de caja", cuerpo: "Un turno va desde que abres la caja con su base de efectivo hasta que cuentas el dinero y cierras. Aquí cuadras lo contado contra lo vendido y revisas los turnos anteriores." },
      {
        selector: "[title=\"Contar la caja y cerrar el turno abierto\"]",
        titulo: "Cerrar el turno",
        cuerpo: "Cuenta el efectivo de la caja y compara con lo que el sistema espera. Si hay pedidos abiertos, la pantalla de cierre te los muestra antes de cerrar.",
        lado: "bottom",
      },
      {
        selector: ".v-data-table",
        titulo: "Cuadre de cada turno",
        cuerpo: {
          restaurante: "Por turno ves el monto inicial, el total de ventas, lo registrado al cerrar y la diferencia, además de domicilios y propinas. Con los botones imprimes el reporte del turno.",
          defecto: "Por turno ves el monto inicial, el total de ventas, lo registrado al cerrar y la diferencia, además de domicilios. Con los botones imprimes el reporte del turno.",
        },
        lado: "top",
      },
      fin,
    ],
  },
  reportes: {
    ruta: /^\/pos\/(reportes|local-orders|server-orders|kitchen-times|orders-receive)\/?$/,
    pasos: [
      {
        titulo: "Reportes y órdenes",
        cuerpo: {
          restaurante: "Revisa lo que pasó en la caja: órdenes con su estado, los pedidos que llegaron de los meseros y los tiempos de cocina.",
          defecto: "Revisa lo que pasó en la caja: las órdenes con su estado, para buscarlas e imprimirlas.",
        },
      },
      { selector: ".seccion-fichas__rejilla", titulo: "Elige qué mirar", cuerpo: "Cada ficha abre un reporte distinto.", lado: "bottom" },
      { selector: ".v-data-table, .v-card", titulo: "Busca y reimprime", cuerpo: "Filtra por fecha o estado, abre una orden para ver el detalle y reimprime el recibo si el cliente lo pide.", lado: "bottom" },
      fin,
    ],
  },
  agotados: {
    ruta: /^\/pos\/agotados\/?$/,
    pasos: [
      {
        titulo: "Productos agotados",
        cuerpo: {
          restaurante: "Marca lo que se acabó para que no se siga vendiendo (por ejemplo, un plato sin ingredientes). Cuando vuelva, lo reactivas.",
          drogueria: "Marca lo que se acabó para que no se siga vendiendo. Cuando llegue el pedido, lo reactivas.",
          defecto: "Marca lo que se acabó para que no se siga vendiendo. Cuando llegue la mercancía, lo reactivas.",
        },
      },
      { selector: ".agotados .v-card", titulo: "Busca y marca", cuerpo: "Busca el producto y toca «Marcar agotado». En la lista de arriba están los que ya no se venden; «Volver a vender» lo reactiva.", lado: "bottom" },
      fin,
    ],
  },
  gastos: {
    ruta: /^\/pos\/expense/,
    pasos: [
      { titulo: "Egresos de caja", cuerpo: "Registra lo que sale de la caja (pagos a proveedores, gastos menores). Quedan descontados en el cuadre del turno." },
      { selector: ".v-data-table, .v-card", titulo: "Borradores y aprobados", cuerpo: "Un egreso en borrador todavía se puede editar; al aprobarlo queda fijo en el cuadre. Los anulados quedan como historia.", lado: "bottom" },
      fin,
    ],
  },
  devoluciones: {
    ruta: /^\/pos\/(de-)?credit-note|^\/pos\/de-credit-note/,
    pasos: [
      { titulo: "Notas crédito (devoluciones)", cuerpo: "Cuando un cliente devuelve una venta ya facturada, se hace una nota crédito. Aquí ves las aprobadas, las anuladas y las que están en borrador." },
      { selector: ".v-data-table, .v-card", titulo: "Consulta", cuerpo: "Busca la nota, ábrela para ver qué se devolvió e imprime el comprobante.", lado: "bottom" },
      fin,
    ],
  },
  tarjetas: {
    ruta: /^\/pos\/gift-card/,
    pasos: [
      { titulo: "Tarjetas de regalo", cuerpo: "Aquí ves las tarjetas, su saldo y su estado. Una tarjeta nueva se activa en la caja al venderla; pasa a «Activa» y ya se puede pagar con ella." },
      fin,
    ],
  },
  ajustes: {
    ruta: /^\/pos\/(ajustes|configuration|tools|motivos-de-anulacion)\/?$/,
    pasos: [
      {
        titulo: "Configuración de la caja",
        cuerpo: {
          restaurante: "La caja, las impresoras (recibos, cocina y bar), los motivos de anulación, la sincronización con el servidor y las herramientas.",
          defecto: "La caja, las impresoras, la sincronización con el servidor y las herramientas.",
        },
      },
      { selector: ".seccion-fichas__rejilla", titulo: "Elige qué ajustar", cuerpo: "Cada ficha abre un grupo de opciones. Estos cambios afectan a esta caja; si dudas de una opción, pregunta antes de cambiarla.", lado: "bottom" },
      fin,
    ],
  },

  // Solo existen en la app de escritorio.
  equipo: {
    ruta: /^\/pos\/equipo\/?$/,
    pasos: [
      { titulo: "Impresoras y balanza", cuerpo: "Lo que configures aquí se guarda en este equipo: la caja imprime y pesa por su cuenta, sin el agente de impresión." },
      { selector: "#tarjeta-recibos", titulo: "Recibos", cuerpo: "Elige la impresora de recibos y el ancho del papel. Con una impresora elegida, los recibos salen solos, sin el cuadro de impresión.", lado: "bottom" },
      {
        selector: "#tarjeta-comandas",
        solo: "restaurante",
        titulo: "Cocina y bar",
        cuerpo: "Agrega una impresora por puesto (cocina, bar…) y elige qué categorías salen por cada una. Lo que no asignes sale por la impresora principal.",
        lado: "top",
      },
      { selector: "#tarjeta-etiquetas", titulo: "Etiquetas", cuerpo: "La impresora de etiquetas de códigos de barras (Zebra o compatible con ZPL).", lado: "top" },
      { selector: "#tarjeta-balanza", titulo: "Balanza", cuerpo: "El puerto y el modo de tu balanza. Con «Probar balanza» ves el peso que llega antes de usarla en una venta.", lado: "top" },
      { selector: "#btn-guardar-equipo", titulo: "Guardar", cuerpo: "Los cambios de recibos, etiquetas y balanza se guardan con este botón.", lado: "top" },
      fin,
    ],
  },
  etiquetas: {
    ruta: /^\/pos\/etiquetas\/?$/,
    pasos: [
      { titulo: "Etiquetas de códigos de barras", cuerpo: "Imprime las etiquetas del inventario directo en la impresora de etiquetas de esta caja. Necesitan internet: las arma el servidor." },
      { selector: "#select-lista-etiquetas", titulo: "Lista de precios y formato", cuerpo: "Elige la lista de precios que va en la etiqueta y el formato (tamaño).", lado: "bottom" },
      { selector: "#buscar-producto-etiquetas", titulo: "Productos", cuerpo: "Busca los productos y ajusta cuántas etiquetas de cada uno. Solo cambia lo que se imprime, no el producto.", lado: "bottom" },
      { selector: "#btn-imprimir-etiquetas", titulo: "Imprimir", cuerpo: "Imprime los de la lista, o todo el inventario con el otro botón.", lado: "top" },
      fin,
    ],
  },
};

/** ¿El elemento (recorrido o paso) aplica a este tipo de negocio? */
function aplica(solo, tipo) {
  return !solo || [].concat(solo).includes(tipo);
}

/** Texto de un paso para un tipo de negocio: una cadena, o el que le toca de un objeto con `defecto`. */
export function textoPara(texto, tipo) {
  if (texto && typeof texto === "object") return texto[tipo] !== undefined ? texto[tipo] : texto.defecto;
  return texto;
}

/** Pasos del recorrido para este tipo de negocio, con el texto ya resuelto. */
export function pasosDe(clave, tipo) {
  const t = TOURS[clave];
  if (!t || !aplica(t.solo, tipo)) return [];
  return t.pasos
    .filter((p) => aplica(p.solo, tipo))
    .map((p) => ({ ...p, titulo: textoPara(p.titulo, tipo), cuerpo: textoPara(p.cuerpo, tipo) }));
}

/** Clave del recorrido de una ruta (los de ventana no cuentan), o null si la pantalla no tiene. */
export function tourDeRuta(path, tipo) {
  for (const [clave, t] of Object.entries(TOURS)) {
    if (!t.aparece && t.ruta.test(path) && aplica(t.solo, tipo)) return clave;
  }
  return null;
}

/** Recorridos de ventana de esta ruta y tipo de negocio: [[clave, selector]]. */
export function toursDeVentana(path, tipo) {
  return Object.entries(TOURS)
    .filter(([, t]) => t.aparece && t.ruta.test(path) && aplica(t.solo, tipo))
    .map(([clave, t]) => [clave, t.aparece]);
}
